/**
 * FounderCapabilityStatusView — read-only Founder capability-status
 * panel mounted in Settings → Advanced.
 *
 * Bounded, non-actionable presentation of the v1 client capability
 * contract (server: `GET /api/client/capabilities`). Consumes the
 * `useClientCapabilities` hook exclusively for state — no direct
 * network calls, no client-side Founder probe, no action buttons
 * that would imply the UI can grant / activate anything.
 *
 * Guarantees:
 *   * Renders INDEPENDENT of `getSettings` — mounts in
 *     `SettingsPage.jsx` outside the `settings &&` gate so a failed
 *     user-settings fetch never masks capability truth.
 *   * The four axes are presented DISTINCTLY per fact so a founder
 *     cannot confuse them:
 *       (a) declared_enabled       — a declaration, not a proof.
 *       (b) implementation_status  — code path present in the build.
 *       (c) enablement             — server-scoped operational hold.
 *       (d) readiness              — governance-verified for THIS
 *                                    surface.
 *     "unknown" / "unverified" is NEVER rendered as "ready" — every
 *     chip carries its own value tag and its own visual treatment.
 *   * Bounded state coverage: loading (auth or fetch in flight),
 *     signed-out, non-Founder (authoritative 403 masked identically
 *     to signed-out per Root privacy note), error kinds
 *     (network_error / server_error / incompatible) with a retry
 *     control, and empty (server returned no validated facts).
 *   * Voice-scope precision: the ONLY capability that inherits the
 *     "conversation guard" scope note is the exact grounded id
 *     `voice_conversation`. A voice-like id / label alone does NOT
 *     inherit that scope — the scope text is anchored to the manifest
 *     id, not to substring heuristics.
 *   * `scope_note` from the fact itself, when provided, is ALWAYS
 *     rendered as its own visible element regardless of hold state.
 *     It is not treated as a fallback reason.
 *
 * NOT included by design:
 *   * activation / enable / disable / retry-with-side-effects actions
 *   * provider names, model names, latency, or any runtime figure
 *   * any navigation change or new route
 *   * internal governance wording in product-facing copy
 */
import { useClientCapabilities } from "@/hooks/useClientCapabilities";

// ---------------------------------------------------------------------------
// Grounded manifest ids. The set is intentionally SMALL and exact —
// the presentation layer must never widen scope beyond ids the manifest
// actually declares. Substring / label heuristics were rejected during
// Root review because they can misattribute scope to unrelated facts.
// ---------------------------------------------------------------------------

/**
 * The exact grounded id for the realtime conversation capability. This
 * is the ONLY capability whose scope explicitly covers the realtime
 * client-secret exchange and the conversation surface. Voice sampling
 * (e.g. `/voice-sample`) is a separate surface and is NOT covered by
 * this scope.
 */
export const VOICE_CONVERSATION_ID = "voice_conversation";

// ---------------------------------------------------------------------------
// Static, bounded copy strings.
// ---------------------------------------------------------------------------

const COPY = Object.freeze({
    heading: "Founder capability status",
    subtitle:
        "Read-only view. The server is the sole authority on Founder access " +
        "and capability enablement. Nothing here activates or overrides " +
        "anything.",
    loading: "Loading capability status…",
    signedOut:
        "Sign in as the Founder to view capability status. This surface stays " +
        "empty until an authenticated session is present.",
    nonFounder:
        "This account is not authorized to view the Founder capability " +
        "manifest.",
    empty:
        "No validated capability status is available for this surface. The " +
        "capability service may have returned no facts, or all returned " +
        "facts failed contract validation and were dropped.",
    genericHoldPrefix: "Enablement held on this surface.",
    retry: "Retry",
    axisLabels: Object.freeze({
        declared: "declared",
        implementation: "implementation",
        enablement: "enablement",
        readiness: "readiness",
    }),
    errors: Object.freeze({
        network_error:
            "Couldn't reach the capability service. Check the connection and " +
            "try again.",
        server_error:
            "The capability service returned an error. Try again in a moment.",
        incompatible:
            "The response from the capability service could not be parsed " +
            "against a supported contract — either the version is not one " +
            "this client understands, or the payload was malformed. A reload " +
            "is not guaranteed to resolve it.",
    }),

    // Voice-conversation-specific scope copy. Distinct per enablement
    // state. All four state strings share the same voice-scope frame
    // (realtime client-secret + conversation surface) and all explicitly
    // note that `/voice-sample` sits OUTSIDE this guard.
    voiceConversationScope: Object.freeze({
        held:
            "Scope: the realtime client-secret exchange and the conversation " +
            "surface are the ONLY things this hold covers. Voice sampling at " +
            "`/voice-sample` is outside this guard and is not affected by " +
            "this hold.",
        not_held:
            "Scope: the realtime client-secret exchange and the conversation " +
            "surface report as not held on this surface. This alone does " +
            "not establish full-pipeline readiness — every other axis " +
            "(implementation, readiness) still applies independently, and " +
            "voice sampling at `/voice-sample` is a separate surface " +
            "outside this guard.",
        dormant:
            "Scope: the realtime client-secret exchange and the conversation " +
            "surface report as dormant on this surface. Voice sampling at " +
            "`/voice-sample` is a separate surface outside this guard.",
        unknown:
            "Scope: the realtime client-secret exchange and the conversation " +
            "surface report as unknown on this surface. Voice sampling at " +
            "`/voice-sample` is a separate surface outside this guard.",
    }),
});

// ---------------------------------------------------------------------------
// Fact-level presentation helpers. Pure — exported for direct testing.
// ---------------------------------------------------------------------------

/**
 * Compose the hold-note text for a fact whose enablement is "held".
 * This function no longer decides voice scope — voice scope is
 * resolved by an EXACT id match downstream. Non-held facts return
 * null. For held facts we return a bounded generic hold prefix
 * without any reason interpolation; `enablement_reason` and
 * `scope_note` render as their own elements (see `composeReasonNote`
 * and `composeScopeNote`) so a founder can distinguish "the hold
 * exists" from "here is the reason" from "here is the scope".
 */
export function composeHoldNote(fact) {
    if (!fact || fact.enablement !== "held") return null;
    return COPY.genericHoldPrefix;
}

/**
 * Return the fact's validated `enablement_reason` string, or null if
 * absent / empty. Callers render this as its own element (never as a
 * fallback for scope, never merged into hold text).
 */
export function composeReasonNote(fact) {
    if (!fact || typeof fact !== "object") return null;
    if (fact.enablement !== "held") return null;
    const r = fact.enablement_reason;
    return typeof r === "string" && r.length > 0 ? r : null;
}

/**
 * Return the fact's validated `scope_note` string, or null if absent /
 * empty. ALWAYS returned regardless of enablement state — a fact that
 * provides a scope note supplies it as its own element, not as a
 * fallback for anything else.
 */
export function composeScopeNote(fact) {
    if (!fact || typeof fact !== "object") return null;
    const s = fact.scope_note;
    return typeof s === "string" && s.length > 0 ? s : null;
}

/**
 * Voice-scope resolver. The ONLY capability that inherits the
 * realtime-conversation scope text is the exact grounded id
 * `voice_conversation`. A voice-like label / non-canonical id does
 * NOT inherit this scope. Returns the state-specific scope string
 * from `COPY.voiceConversationScope`, or null when the fact is not
 * the canonical conversation capability.
 */
export function composeVoiceConversationScope(fact) {
    if (!fact || typeof fact !== "object") return null;
    if (fact.id !== VOICE_CONVERSATION_ID) return null;
    const state = fact.enablement;
    // Only render the scope text for enablement literals we have
    // grounded copy for. Any other literal returns null so we never
    // fabricate a scope claim.
    if (
        state !== "held" &&
        state !== "not_held" &&
        state !== "dormant" &&
        state !== "unknown"
    ) {
        return null;
    }
    return COPY.voiceConversationScope[state] || null;
}

// Human-facing axis value labels. Keep the raw literal string as the
// visible token so the founder sees exactly the manifest word — a
// friendlier synonym would obscure the four axes' distinctness.
const AXIS_VALUE_LABEL = Object.freeze({
    declared: (v) => (v === true ? "yes" : v === false ? "no" : "unknown"),
    implementation: (v) =>
        v === "known_implemented" ? "known_implemented" : "unknown",
    enablement: (v) =>
        ["held", "dormant", "not_held", "unknown"].includes(v) ? v : "unknown",
    readiness: (v) => (v === "verified" ? "verified" : "unverified"),
});

function axisTone(axis, value) {
    if (axis === "declared") return value === true ? "positive" : "neutral";
    if (axis === "implementation")
        return value === "known_implemented" ? "positive" : "neutral";
    if (axis === "enablement") {
        if (value === "not_held") return "positive";
        if (value === "held") return "warning";
        return "neutral";
    }
    if (axis === "readiness") return value === "verified" ? "positive" : "neutral";
    return "neutral";
}

const TONE_CLASSES = Object.freeze({
    positive: "border-qoryn-cyan/40 text-qoryn-cyan bg-qoryn-cyan/10",
    warning: "border-amber-400/40 text-amber-300 bg-amber-400/10",
    neutral: "border-white/10 text-qoryn-textDim bg-white/[0.03]",
});

// ---------------------------------------------------------------------------
// Presentational sub-parts. All small, all self-contained.
// ---------------------------------------------------------------------------

function AxisChip({ axis, value, testId }) {
    const tone = axisTone(axis, value);
    const label = COPY.axisLabels[axis];
    const valueLabel = AXIS_VALUE_LABEL[axis](value);
    return (
        <span
            data-testid={testId}
            data-axis={axis}
            data-value={String(value)}
            data-tone={tone}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono-ui text-[9px] tracking-widest uppercase ${TONE_CLASSES[tone]}`}
        >
            <span className="opacity-70">{label}</span>
            <span>{valueLabel}</span>
        </span>
    );
}

function CapabilityRow({ fact }) {
    const holdNote = composeHoldNote(fact);
    const reasonNote = composeReasonNote(fact);
    const scopeNote = composeScopeNote(fact);
    const voiceScope = composeVoiceConversationScope(fact);
    const isCanonicalVoiceConversation = fact.id === VOICE_CONVERSATION_ID;
    return (
        <li
            data-testid={`fcs-row-${fact.id}`}
            data-canonical-voice-conversation={
                isCanonicalVoiceConversation ? "true" : "false"
            }
            className="space-y-2 rounded-xl border border-qoryn-border/40 bg-white/[0.02] p-3"
        >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span
                    data-testid={`fcs-row-${fact.id}-label`}
                    className="font-sans text-[13px] text-qoryn-text"
                >
                    {fact.label}
                </span>
                <span
                    data-testid={`fcs-row-${fact.id}-id`}
                    className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted"
                >
                    {fact.id}
                </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
                <AxisChip
                    axis="declared"
                    value={fact.declared_enabled}
                    testId={`fcs-row-${fact.id}-axis-declared`}
                />
                <AxisChip
                    axis="implementation"
                    value={fact.implementation_status}
                    testId={`fcs-row-${fact.id}-axis-implementation`}
                />
                <AxisChip
                    axis="enablement"
                    value={fact.enablement}
                    testId={`fcs-row-${fact.id}-axis-enablement`}
                />
                <AxisChip
                    axis="readiness"
                    value={fact.readiness}
                    testId={`fcs-row-${fact.id}-axis-readiness`}
                />
            </div>
            {holdNote && (
                <p
                    data-testid={`fcs-row-${fact.id}-hold-note`}
                    className="font-sans text-[12px] leading-relaxed text-qoryn-textDim"
                >
                    {holdNote}
                </p>
            )}
            {reasonNote && (
                <p
                    data-testid={`fcs-row-${fact.id}-reason-note`}
                    className="font-sans text-[12px] leading-relaxed text-qoryn-textDim"
                >
                    <span className="font-mono-ui text-[9px] tracking-widest uppercase opacity-70">
                        reason:{" "}
                    </span>
                    {reasonNote}
                </p>
            )}
            {scopeNote && (
                <p
                    data-testid={`fcs-row-${fact.id}-scope-note`}
                    className="font-sans text-[12px] leading-relaxed text-qoryn-textDim"
                >
                    <span className="font-mono-ui text-[9px] tracking-widest uppercase opacity-70">
                        scope:{" "}
                    </span>
                    {scopeNote}
                </p>
            )}
            {voiceScope && (
                <p
                    data-testid={`fcs-row-${fact.id}-voice-conversation-scope`}
                    data-scope-state={fact.enablement}
                    className="font-sans text-[12px] leading-relaxed text-qoryn-textDim"
                >
                    {voiceScope}
                </p>
            )}
        </li>
    );
}

function StatusMessage({ testId, tone = "neutral", children, action = null }) {
    const toneClass =
        tone === "warning"
            ? "border-amber-400/30 text-amber-200"
            : "border-white/10 text-qoryn-textDim";
    return (
        <div
            data-testid={testId}
            className={`rounded-xl border ${toneClass} bg-white/[0.02] p-4 space-y-3`}
        >
            <p className="font-sans text-[13px] leading-relaxed">{children}</p>
            {action}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Component entry
// ---------------------------------------------------------------------------

export default function FounderCapabilityStatusView({ deps } = {}) {
    const { state, errorKind, contract, refresh } = useClientCapabilities(
        deps ? { deps } : undefined,
    );

    let body;
    if (state === "auth_loading" || state === "loading") {
        body = (
            <StatusMessage testId="fcs-loading">{COPY.loading}</StatusMessage>
        );
    } else if (state === "signed_out") {
        body = (
            <StatusMessage testId="fcs-auth-required">
                {COPY.signedOut}
            </StatusMessage>
        );
    } else if (state === "error" && errorKind === "signin_required") {
        body = (
            <StatusMessage testId="fcs-auth-required">
                {COPY.signedOut}
            </StatusMessage>
        );
    } else if (state === "error" && errorKind === "non_founder") {
        body = (
            <StatusMessage testId="fcs-non-founder">
                {COPY.nonFounder}
            </StatusMessage>
        );
    } else if (state === "error") {
        const message =
            COPY.errors[errorKind] ||
            COPY.errors.server_error; /* fallback = generic server_error */
        body = (
            <StatusMessage
                testId="fcs-error"
                tone="warning"
                action={
                    <button
                        type="button"
                        data-testid="fcs-retry-btn"
                        onClick={refresh}
                        className="inline-flex items-center rounded-full border border-qoryn-cyan/40 bg-qoryn-cyan/10 px-3 py-1 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-cyan hover:bg-qoryn-cyan/15 qoryn-focus"
                    >
                        {COPY.retry}
                    </button>
                }
            >
                <span data-testid="fcs-error-message" data-error-kind={errorKind}>
                    {message}
                </span>
            </StatusMessage>
        );
    } else {
        // state === "ok"
        const facts =
            contract && Array.isArray(contract.capabilities)
                ? contract.capabilities
                : [];
        if (facts.length === 0) {
            body = (
                <StatusMessage testId="fcs-empty">{COPY.empty}</StatusMessage>
            );
        } else {
            body = (
                <ul
                    data-testid="fcs-list"
                    data-count={String(facts.length)}
                    className="space-y-2"
                >
                    {facts.map((fact) => (
                        <CapabilityRow key={fact.id} fact={fact} />
                    ))}
                </ul>
            );
        }
    }

    return (
        <div
            data-testid="settings-advanced-founder-capabilities"
            data-state={state}
            data-error-kind={errorKind ?? ""}
            className="qoryn-panel rounded-2xl p-6 space-y-4"
        >
            <div>
                <h3
                    data-testid="fcs-heading"
                    className="font-sans text-[15px] text-qoryn-text"
                >
                    {COPY.heading}
                </h3>
                <p
                    data-testid="fcs-subtitle"
                    className="mt-1 font-sans text-[12px] text-qoryn-textDim leading-relaxed"
                >
                    {COPY.subtitle}
                </p>
            </div>
            {body}
        </div>
    );
}

// Named exports for direct testing of the small helpers and copy.
export { COPY as __copy__ };
